import {
  ICONS_ENUM,
  INavigationDrawer,
  NavigationDrawer
} from '@sanlam-glacier/app-common/components';
import { useConfig } from 'providers/config/ConfigProvider';
import { useEffect } from 'react';
import { UIMatch, useMatches, useNavigate } from 'react-router-dom';
import './SideNavMenu.scss';
import { routes } from 'routes/route-paths';
import { useFeature } from 'components/atoms/feature-flags/useFeature';
import { useUserContext } from 'user-context-manager/user-context-manager';
import { canAccessAny } from 'user-context-manager/can-access';
import { SecurityAccessFeaturesEnum } from 'bff';

type Handle = {
  navigationPath?: string;
};

interface SidNavMenuProps {
  mountSideNav?: () => void;
}

type IData = Required<INavigationDrawer>['data'][number];
type IDataMenuItem = IData['items'][number];
type IItem = Required<INavigationDrawer>['activeItem'];

export const SideNavMenu = ({ mountSideNav }: SidNavMenuProps) => {
  useEffect(() => {
    mountSideNav?.();
  }, [mountSideNav]);
  const matches = useMatches() as UIMatch<unknown, Handle>[];
  const matchingRoutePath =
    [...matches].reverse().find((match) => match.handle?.navigationPath)?.handle?.navigationPath ??
    routes.dashboard;

  const navigate = useNavigate();
  const { routerBasePath } = useConfig();
  const shouldShowDashboard = useFeature('dashboard.showDashboard');

  const userContext = useUserContext();
  const features = userContext.getUserFeatures();

  const navigationItems = [
    {
      id: 'primary-navigation',
      title: 'Primary links',
      items: []
    }
  ] as IData[];

  if (shouldShowDashboard) {
    navigationItems[0]?.items.push({
      id: routes.dashboard,
      title: 'Dashboard',
      icon: 'action-2_dashboard'
    } as IDataMenuItem);
  }

  navigationItems[0]?.items.push({
    id: routes.investments,
    title: 'My investments',
    icon: ICONS_ENUM.EDITOR_INSERT_CHART
  } as IDataMenuItem);

  if (canAccessAny(features, [SecurityAccessFeaturesEnum.OutgoingCorrespondence])) {
    navigationItems[0]?.items.push({
      id: routes.correspondence,
      title: 'Outgoing correspondence',
      icon: ICONS_ENUM.CONTENT_MAIL
    } as IDataMenuItem);
  }
  if (canAccessAny(features, [SecurityAccessFeaturesEnum.FormsAndBrochures])) {
    navigationItems[0]?.items.push({
      //id: routes.formsAndBrochures,
      title: 'Forms & brochures',
      icon: ICONS_ENUM.AV_LIBRARY_BOOKS
    } as IDataMenuItem);
  }

  navigationItems.push({
    id: 'secondary-navigation',
    title: 'Secondary links',
    items: []
  } as IData);

  navigationItems[1]?.items.push({
    id: routes.otherApps,
    title: 'Other apps',
    icon: 'navigation_apps'
  } as IDataMenuItem);

  if (canAccessAny(features, [SecurityAccessFeaturesEnum.Legal])) {
    navigationItems[1]?.items.push({
      id: routes.legal,
      title: 'Legal',
      icon: 'action-2_secured-file'
    } as IDataMenuItem);
  }

  navigationItems[1]?.items.push({
    id: routes.support,
    title: 'Support',
    icon: 'action-2_contact-support'
  } as IDataMenuItem);

  const activeItem = navigationItems
    .flatMap((x) => x.items)
    .find((item) => matchingRoutePath.includes(item.id)) ?? {
    id: routes.dashboard,
    title: 'Dashboard',
    items: []
  };

  const handleNavigate = (clickedItem: IItem) => {
    if (clickedItem.id === routes.legal) {
      const path = `${routerBasePath}/${clickedItem.id}`;
      // remove extra forward slashes to correctly route legal page
      const pathCleanedUp = path.replaceAll(/\/{2,}/g, '/');
      // new tab links, cannot infer routerBasePath here
      window.open(pathCleanedUp, '_blank');
    } else {
      navigate(clickedItem.id);
    }
  };

  return (
    <NavigationDrawer
      className='navigation-drawer'
      data={navigationItems}
      onClick={handleNavigate}
      activeItem={activeItem as IItem}
      showBadge={false}
    />
  );
};
